import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import { labelsAPI } from '../utils/supabase'
import { handleRealtimeLabelEvent } from './reducers/realtimeReducers'

// Async thunks
export const fetchLabels = createAsyncThunk(
  'labels/fetch',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await labelsAPI.getAllLabels(userId)
      if (error) throw error
      return data || []
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createLabel = createAsyncThunk(
  'labels/create',
  async ({ userId, labelData }, { rejectWithValue }) => {
    try {
      const { data, error } = await labelsAPI.createLabel(userId, labelData)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateLabel = createAsyncThunk(
  'labels/update',
  async ({ labelId, updates }, { rejectWithValue }) => {
    try {
      const { data, error } = await labelsAPI.updateLabel(labelId, updates)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteLabel = createAsyncThunk(
  'labels/delete',
  async (labelId, { rejectWithValue }) => {
    try {
      const { data, error } = await labelsAPI.deleteLabel(labelId)
      if (error) throw error
      return labelId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const bulkUpdateTaskLabels = createAsyncThunk(
  'labels/bulkUpdateTaskLabels',
  async ({ taskId, labelIds, userId }, { rejectWithValue }) => {
    try {
      const { data, error } = await labelsAPI.bulkUpdateTaskLabels(taskId, labelIds, userId)
      if (error) throw error
      return { taskId, labelIds: data }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  labels: [],
  loading: false,
  error: null,
  initialized: false
}

const labelsSlice = createSlice({
  name: 'labels',
  initialState,
  reducers: {
    setLabels: (state, action) => {
      state.labels = action.payload
      state.initialized = true
    },
    clearLabels: (state) => {
      state.labels = []
      state.initialized = false
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    // Optimistic update for label modifications
    updateLabelOptimistic: (state, action) => {
      const { labelId, updates } = action.payload
      const index = state.labels.findIndex(label => label.id === labelId)
      if (index !== -1) {
        state.labels[index] = { ...state.labels[index], ...updates }
      }
    },
    // Rollback optimistic updates on failure
    rollbackLabelUpdate: (state, action) => {
      const { labelId, previousData } = action.payload
      const index = state.labels.findIndex(label => label.id === labelId)
      if (index !== -1) {
        state.labels[index] = previousData
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Labels
      .addCase(fetchLabels.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.loading = false
        state.labels = action.payload
        state.initialized = true
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Create Label
      .addCase(createLabel.pending, (state) => {
        state.error = null
      })
      .addCase(createLabel.fulfilled, (state, action) => {
        state.labels.push(action.payload)
      })
      .addCase(createLabel.rejected, (state, action) => {
        state.error = action.payload
      })

      // Update Label
      .addCase(updateLabel.pending, (state) => {
        state.error = null
      })
      .addCase(updateLabel.fulfilled, (state, action) => {
        const index = state.labels.findIndex(label => label.id === action.payload.id)
        if (index !== -1) {
          state.labels[index] = action.payload
        }
      })
      .addCase(updateLabel.rejected, (state, action) => {
        state.error = action.payload
      })

      // Delete Label
      .addCase(deleteLabel.pending, (state) => {
        state.error = null
      })
      .addCase(deleteLabel.fulfilled, (state, action) => {
        state.labels = state.labels.filter(label => label.id !== action.payload)
      })
      .addCase(deleteLabel.rejected, (state, action) => {
        state.error = action.payload
      })

      // Realtime label event handler
      .addCase(handleRealtimeLabelEvent.fulfilled, (state, action) => {
        if (!action.payload) return

        const { type, record, oldRecord } = action.payload

        switch (type) {
          case 'INSERT': {
            // Add new label (avoid duplicates)
            if (!state.labels.find(label => label.id === record.id)) {
              state.labels.push(record)
            }
            break
          }

          case 'UPDATE': {
            // Update existing label
            const index = state.labels.findIndex(label => label.id === record.id)
            if (index !== -1) {
              state.labels[index] = record
            }
            break
          }

          case 'DELETE': {
            // Remove deleted label
            const labelToDelete = oldRecord || record
            if (labelToDelete?.id) {
              state.labels = state.labels.filter(label => label.id !== labelToDelete.id)
            }
            break
          }
        }
      })
  }
})

export const {
  setLabels,
  clearLabels,
  clearError,
  updateLabelOptimistic,
  rollbackLabelUpdate
} = labelsSlice.actions

// Selectors
export const selectLabels = (state) => state.labels.labels
export const selectLabelsLoading = (state) => state.labels.loading
export const selectLabelsError = (state) => state.labels.error
export const selectLabelsInitialized = (state) => state.labels.initialized

// Get label by ID
export const selectLabelById = createSelector(
  [selectLabels, (state, labelId) => labelId],
  (labels, labelId) => labels.find(label => label.id === labelId)
)

// Get labels by IDs (for displaying task labels)
export const selectLabelsByIds = createSelector(
  [selectLabels, (state, labelIds) => labelIds],
  (labels, labelIds) => {
    if (!labelIds || labelIds.length === 0) return []
    return labels.filter(label => labelIds.includes(label.id))
  }
)

// Get labels sorted by name (memoized for performance)
export const selectLabelsSorted = createSelector(
  [selectLabels],
  (labels) => [...labels].sort((a, b) => a.name.localeCompare(b.name))
)

// Get labels with task count (requires tasks from state)
// Only counts open (non-completed, non-archived) tasks
export const selectLabelsWithTaskCount = createSelector(
  [selectLabelsSorted, (state) => state.tasks?.tasks || []],
  (labels, tasks) => {
    return labels.map(label => ({
      ...label,
      taskCount: tasks.filter(task =>
        !task.archived && !task.completed && task.labels && task.labels.some(taskLabel => taskLabel.id === label.id)
      ).length
    }))
  }
)

export default labelsSlice.reducer
